import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
  type UseMutationOptions,
  type UseQueryOptions
} from '@tanstack/vue-query'
import { toValue, type MaybeRefOrGetter } from 'vue'
import { useI18n } from 'vue-i18n'
import { toast } from 'vue-sonner'

import { apiFetch } from './api'

/**
 * Unified Query wrapper that defaults to using apiFetch.
 * 
 * @param queryKey - The unique key for the query
 * @param url - The API endpoint to fetch
 * @param fetchOptions - Options for apiFetch (method, body, etc)
 * @param queryOptions - Additional Vue Query options
 */
export function useApiQuery<TData = any, TError = Error>(
  queryKey: QueryKey,
  url: MaybeRefOrGetter<string>,
  fetchOptions: MaybeRefOrGetter<RequestInit> = {},
  queryOptions: Omit<UseQueryOptions<TData, TError>, 'queryKey' | 'queryFn'> = {}
) {
  return useQuery<TData, TError>({
    queryKey,
    queryFn: () => {
      const targetUrl = toValue(url)
      const options = toValue(fetchOptions)
      // Guard: prevent fetch with null/empty URL (causes "admin/null" style errors)
      if (!targetUrl || typeof targetUrl !== 'string') {
        return Promise.reject(new Error('Query URL is required'))
      }
      return apiFetch<TData>(targetUrl, options)
    },
    ...queryOptions,
  })
}

/**
 * Unified Mutation wrapper with automatic toast notifications.
 * 
 * @param mutationFn - Function that performs the mutation (defaults to apiFetch)
 * @param options - Additional options including success/error messages
 */
export function useApiMutation<TData = any, TError = Error, TVariables = void>(
  options: UseMutationOptions<TData, TError, TVariables> & {
    successMessage?: string | ((data: TData) => string)
    errorMessage?: string | ((error: TError) => string)
  } = {}
) {
  const { t } = useI18n()
  const { successMessage, errorMessage, ...mutationOptions } = options

  return useMutation<TData, TError, TVariables>({
    ...mutationOptions,
    onSuccess: (data, variables, context) => {
      if (successMessage) {
        const message = typeof successMessage === 'function'
          ? successMessage(data)
          : successMessage
        toast.success(message)
      }
      options.onSuccess?.(data, variables, context)
    },
    onError: (error, variables, context) => {
      if (errorMessage) {
        const message = typeof errorMessage === 'function'
          ? errorMessage(error)
          : errorMessage
        toast.error(message)
      } else {
        // Fallback error message if none provided
        const message = error instanceof Error ? error.message : t('shared.error.unexpected')
        toast.error(message)
      }
      options.onError?.(error, variables, context)
    },
  })
}

/**
 * Re-export core TanStack Query utilities for convenience
 */
export { useQueryClient, type QueryKey }
